import * as React from "react";
import { ArrowRight, CircleXIcon, CornerDownLeftIcon, HistoryIcon, Loader2, ShieldAlert } from "lucide-react";

import * as Api from "ApiContracts/control/api/api";
import * as ApiUtils from "ApiUtils";
import * as CustomHooks from "CustomHooks";
import * as TelemetryUtils from "TelemetryUtils";
import * as pubsub from "ApiContracts/subtrace/pubsub/pubsub";
import { Overlay } from "Overlay";
import { SetupInstructions } from "SetupInstructions";

const enum FilterKind {
  Method = 0,
  Status = 1,
  Url = 2,
  Duration = 3,
}

type Filter = {
  text: string;
  kinds: FilterKind[];
  useCount: number;
  lastUsed: number;
  builtIn: boolean;
};

function DevTools(props: { url: string; namespaceId: string }) {
  const { namespaceId } = props;
  const [appliedFilters, setAppliedFilters] = React.useState<string[]>([]);
  const [pendingFilters, setPendingFilters] = React.useState<string[] | undefined>(undefined);
  const [editingFilter, setEditingFilter] = React.useState<string | null>(null);

  const [filterError, setFilterError] = React.useState<string | null>(null);
  const [isApplying, setApplying] = React.useState<boolean>(false);
  const [input, setInput] = React.useState<string>("");

  const getSuggestions = React.useCallback(
    (allSuggestions: Filter[], input: string): Filter[] => {
      const NUM_SUGGESTIONS_SHOWN = 7;
      const inputLower = input.toLowerCase();

      const appliedKinds = new Set<FilterKind>();
      for (const f of appliedFilters) {
        if (f.includes("request.method")) appliedKinds.add(FilterKind.Method);
        if (f.includes("response.status")) appliedKinds.add(FilterKind.Status);
        if (f.includes("request.url")) appliedKinds.add(FilterKind.Url);
        if (f.includes("duration")) appliedKinds.add(FilterKind.Duration);
      }

      const used: Filter[] = [];
      const kindOrder = [FilterKind.Method, FilterKind.Status, FilterKind.Url, FilterKind.Duration];
      const unusedByKind = new Map<FilterKind, Filter[]>(kindOrder.map((k) => [k, []]));

      for (const s of allSuggestions) {
        if (!s.text.toLowerCase().includes(inputLower)) continue;
        if (s.kinds.some((k) => appliedKinds.has(k))) continue;

        if (s.useCount > 0) {
          used.push(s);
        } else {
          const kind = s.kinds[0];
          unusedByKind.get(kind)!.push(s);
        }
      }

      used.sort((a, b) => (a.useCount !== b.useCount ? b.useCount - a.useCount : b.lastUsed - a.lastUsed));

      const result: Filter[] = [];
      for (const s of used) {
        result.push(s);
        if (result.length === NUM_SUGGESTIONS_SHOWN) return result;
      }

      const roundRobinPool: Filter[] = [];
      const indices = new Map(kindOrder.map((k) => [k, 0]));

      while (roundRobinPool.length + result.length < NUM_SUGGESTIONS_SHOWN) {
        let added = false;
        for (const kind of kindOrder) {
          const list = unusedByKind.get(kind)!;
          const idx = indices.get(kind)!;
          if (idx < list.length) {
            roundRobinPool.push(list[idx]);
            indices.set(kind, idx + 1);
            added = true;
          }
          if (roundRobinPool.length + result.length === NUM_SUGGESTIONS_SHOWN) break;
        }
        if (!added) break;
      }

      return [...result, ...roundRobinPool.sort((a, b) => b.kinds[0] - a.kinds[0])];
    },
    [appliedFilters],
  );

  const allSuggestions = React.useRef<Filter[]>([]);
  if (allSuggestions.current.length === 0) {
    allSuggestions.current = loadSuggestions();
  }
  const [suggestionsVisible, setSuggestionsVisible] = React.useState<boolean>(false);
  const defaultSuggestions = React.useMemo(() => getSuggestions(allSuggestions.current, ""), [getSuggestions]);
  const [suggestions, setSuggestions] = React.useState<Filter[]>(defaultSuggestions);
  const [focusedSuggestionIndex, setFocusedSuggestionIndex] = React.useState<number | undefined>(undefined);
  const suggestionRefs = React.useRef<(HTMLSpanElement | null)[]>([]);

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null);

  React.useEffect(() => {
    const handler = (ev: MessageEvent) => {
      if (ev.data.source !== "iframer-bridge") {
        return;
      }

      switch (ev.data.method) {
        case "setFilterError":
          setFilterError(ev.data.args);
          setApplying(false);
          return;
        case "markApplied": {
          const newFilters = pendingFilters?.filter((f) => !appliedFilters.includes(f)) ?? [];
          for (const filter of newFilters) {
            const suggestion = allSuggestions.current.find((s) => s.text === filter);
            if (suggestion) {
              suggestion.useCount++;
              suggestion.lastUsed = Date.now();
            } else {
              const kinds = new Set<FilterKind>();
              if (filter.includes("request.method")) kinds.add(FilterKind.Method);
              if (filter.includes("response.status")) kinds.add(FilterKind.Status);
              if (filter.includes("request.url")) kinds.add(FilterKind.Url);
              if (filter.includes("duration")) kinds.add(FilterKind.Duration);

              allSuggestions.current.push({ builtIn: false, kinds: [...kinds.values()], lastUsed: Date.now(), text: filter, useCount: 1 });
            }
          }

          const updatedSuggestions = allSuggestions.current.sort((a, b) => b.lastUsed - a.lastUsed).slice(0, 100);
          localStorage.setItem(`subtrace.live.filters.${namespaceId}`, JSON.stringify(updatedSuggestions));
          allSuggestions.current = updatedSuggestions;

          setInput("");
          setFilterError(null);
          setApplying(false);
          setEditingFilter(null);
          setAppliedFilters(pendingFilters ?? []);
          setPendingFilters(undefined);
          return;
        }
        case "onEvent":
          return;
        default:
          throw new Error("unknown method");
      }
    };

    window.addEventListener("message", handler);
    return () => {
      console.log("iframer-bridge: parent: removing handler", handler);
      window.removeEventListener("message", handler);
    };
  }, [allSuggestions, appliedFilters, namespaceId, pendingFilters]);

  const commitHash = (window as any).subtrace.version.commitHash;

  const applyFilters = React.useCallback((filters: string[]): void => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;

    win.postMessage(
      {
        source: "iframer-bridge",
        method: "setFilters",
        args: filters,
      },
      "*",
    );

    setApplying(true);
    setPendingFilters(filters);
    setSuggestionsVisible(false);
    setFocusedSuggestionIndex(undefined);
  }, []);

  const cancelEditFilter = React.useCallback((): void => {
    if (editingFilter) {
      setEditingFilter(null);
      setInput("");
      setFilterError(null);
      setSuggestionsVisible(false);
      setFocusedSuggestionIndex(undefined);
    }
  }, [editingFilter]);

  const commitFilter = React.useCallback(
    (filter: string): void => {
      if (!filter) {
        return;
      }

      const normalized = filter.toLowerCase();
      const filters = editingFilter ? appliedFilters.filter((f) => f !== editingFilter) : appliedFilters;

      if (filters.some((f) => f.toLowerCase() === normalized)) {
        return;
      }

      applyFilters([...filters, filter]);
    },
    [appliedFilters, applyFilters, editingFilter],
  );

  const editFilter = React.useCallback(
    (filter: string): void => {
      if (editingFilter != null) {
        return;
      }

      setEditingFilter(filter);
      setInput(filter);
      setSuggestionsVisible(false);
      setFocusedSuggestionIndex(undefined);

      inputRef.current?.focus();
    },
    [editingFilter],
  );

  const onInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent): void => {
      if (event.key === "Enter") {
        commitFilter(input);
      } else if (event.key === "Escape") {
        if (suggestionsVisible && suggestions.length > 0) {
          setSuggestionsVisible(false);
          setFocusedSuggestionIndex(undefined);
        } else if (editingFilter) {
          cancelEditFilter();
        }
      } else if (event.key === " " && event.ctrlKey) {
        event.preventDefault();
        setSuggestionsVisible(true);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        const newIndex = focusedSuggestionIndex != null ? (focusedSuggestionIndex - 1 + suggestions.length) % suggestions.length : suggestions.length - 1;
        setFocusedSuggestionIndex(newIndex);
        suggestionRefs.current[newIndex]?.scrollIntoView({ behavior: "instant", block: "nearest" });
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        const newIndex = focusedSuggestionIndex != null ? (focusedSuggestionIndex + 1) % suggestions.length : 0;
        setFocusedSuggestionIndex(newIndex);
        suggestionRefs.current[newIndex]?.scrollIntoView({ behavior: "instant", block: "nearest" });
      } else if (event.key === "Tab") {
        if (suggestionsVisible && focusedSuggestionIndex != null && focusedSuggestionIndex >= 0 && focusedSuggestionIndex < suggestions.length) {
          event.preventDefault();
          setInput(suggestions[focusedSuggestionIndex].text);
          setSuggestionsVisible(false);
          setFocusedSuggestionIndex(0);
        }
      }
    },
    [cancelEditFilter, commitFilter, editingFilter, focusedSuggestionIndex, input, suggestions, suggestionsVisible],
  );

  const removeFilter = React.useCallback(
    (filter: string): void => {
      applyFilters(appliedFilters.filter((f) => f !== filter));
    },
    [applyFilters, appliedFilters],
  );

  const renderedFilters = React.useMemo((): React.ReactNode => {
    if (appliedFilters.length === 0) {
      return null;
    }

    const sortedFilters = appliedFilters.sort((f1, f2) => {
      const f1Lower = f1.toLocaleLowerCase();
      const f2Lower = f2.toLocaleLowerCase();
      let p1 = 0;
      let p2 = 0;

      if (f1Lower.includes("request.url")) p1 = 1;
      if (f1Lower.includes("response.status")) p1 = 2;
      if (f1Lower.includes("duration")) p1 = 3;
      if (f1Lower.includes("request.method")) p1 = 4;

      if (f2Lower.includes("request.url")) p2 = 1;
      if (f2Lower.includes("response.status")) p2 = 2;
      if (f2Lower.includes("duration")) p2 = 3;
      if (f2Lower.includes("request.method")) p2 = 4;

      if (p1 !== p2) return p1 - p2;

      return f1Lower.localeCompare(f2Lower);
    });

    return (
      <div className="flex flex-wrap space-x-2">
        {sortedFilters.map((filter) => (
          <div
            key={filter}
            onClick={() => editFilter(filter)}
            className={`
              flex select-none justify-between rounded-sm px-1 py-0.5 space-x-1 cursor-pointer
              bg-zinc-900/45 border border-zinc-800/75 font-mono text-zinc-600 text-[8px]
              ${
                editingFilter != null
                  ? editingFilter === filter
                    ? "pointer-events-none bg-zinc-800/60 shadow-[0_1px_3px_0_rgba(113,113,122,0.25)] opacity-100 cursor-auto"
                    : "pointer-events-none opacity-50"
                  : "hover:bg-zinc-900/80"
              }
            
            `}
          >
            <span className="font-mono text-zinc-600 text-[8px]">{filter}</span>
            {editingFilter !== filter ? (
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  removeFilter(filter);
                }}
                disabled={editingFilter != null}
                className={`text-zinc-500 ${editingFilter != null ? "opacity-40 pointer-events-none" : "hover:text-red-500"}`}
              >
                <CircleXIcon size={11} />
              </button>
            ) : (
              <span className="ml-1 h-1.5 w-1.5 rounded-full self-center bg-zinc-700" />
            )}
          </div>
        ))}
      </div>
    );
  }, [appliedFilters, editFilter, editingFilter, removeFilter]);

  const renderHighlightedText = React.useCallback((needle: string, haystack: string): React.ReactNode => {
    if (!needle) {
      return <span>{haystack}</span>;
    }

    const index = haystack.toLowerCase().indexOf(needle.toLowerCase());
    if (index === -1) {
      return <span>{haystack}</span>;
    }

    const before = haystack.slice(0, index);
    const match = haystack.slice(index, index + needle.length);
    const after = haystack.slice(index + needle.length);

    return (
      <span className="flex items-center whitespace-pre">
        <span className="text-zinc-400">{before}</span>
        <span className="font-semibold text-zinc-300">{match}</span>
        <span className="text-zinc-400">{after}</span>
      </span>
    );
  }, []);

  const renderedKeyboardHelp = React.useMemo((): React.ReactNode => {
    if (document.activeElement !== inputRef.current || input === "") {
      return null;
    }

    if (editingFilter != null) {
      return (
        <div className="absolute top-1/2 -translate-y-[50%] right-3 text-zinc-700 bg-zinc-900 text-[10px] flex items-center justify-center select-none">
          <CornerDownLeftIcon size={10} strokeWidth={3} className="text-zinc-500 mx-1" />
          <span>to commit,</span>
          <span className="font-semibold text-[8px] text-zinc-500 bg-zinc-700/60 rounded-sm py-[1px] px-[2px] mx-1">esc</span>
          <span>to discard changes</span>
        </div>
      );
    }

    return (
      <div className="absolute top-1/2 -translate-y-[50%] right-3 text-zinc-700 bg-zinc-900 text-[10px] flex items-center justify-center select-none">
        <CornerDownLeftIcon size={10} strokeWidth={3} className="text-zinc-500 mx-1" />
        <span>to commit</span>
      </div>
    );
  }, [editingFilter, input]);

  const renderedSuggestions = React.useMemo((): React.ReactNode => {
    if (!suggestionsVisible || suggestions.length === 0) {
      return null;
    }

    suggestionRefs.current = [];

    return (
      <div className="absolute w-full top-[calc(4px+100%)] z-10">
        <div className="w-full bg-zinc-800/95 backdrop-blur-sm rounded-md border border-zinc-700/50 shadow-xl overflow-hidden">
          <div className="[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-zinc-600/50 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-zinc-500/50">
            <div className="flex flex-col">
              {suggestions.map((suggestion, index) => {
                return (
                  <span
                    key={suggestion.text}
                    className={`font-mono font-light text-xs px-3 py-1.5 cursor-pointer flex items-center justify-between whitespace-nowrap text-zinc-400 hover:bg-zinc-700/50
                    ${focusedSuggestionIndex === index ? "bg-zinc-700/80 hover:bg-zinc-700/80" : ""}`}
                    onClick={() => commitFilter(suggestion.text)}
                    ref={(el) => {
                      suggestionRefs.current[index] = el;
                    }}
                  >
                    <span>{focusedSuggestionIndex === index ? renderHighlightedText(input, suggestion.text) : <span>{suggestion.text}</span>}</span>
                    {suggestion.useCount > 0 ? <HistoryIcon className="h-3 w-3 text-zinc-500" /> : null}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }, [commitFilter, focusedSuggestionIndex, input, renderHighlightedText, suggestions, suggestionsVisible]);

  return (
    <div className="w-full flex flex-col space-y-2 p-1">
      <div className="w-full rounded flex space-x-2">
        <div className="w-full relative group">
          <input
            autoFocus
            type="text"
            onChange={(ev) => {
              const value = ev.target.value;
              setInput(value);
              setSuggestions(getSuggestions(allSuggestions.current, value));
              setSuggestionsVisible(value !== "");
              setFocusedSuggestionIndex(value === "" ? undefined : 0);
              setFilterError(null);
            }}
            onKeyDown={onInputKeyDown}
            className="w-full rounded font-mono text-[11px] font-light placeholder:text-zinc-700 text-zinc-300 px-3 py-[6px] bg-zinc-900 border border-zinc-700/75 hover:border-zinc-600/75 focus:outline-0 focus:border-zinc-600"
            placeholder='example: request.method == "POST"'
            ref={inputRef}
            value={input}
          />
          {renderedKeyboardHelp}
          {renderedSuggestions}
        </div>
        <button
          onClick={() => {
            if (input !== "" && !appliedFilters.map((f) => f !== editingFilter && f.toLowerCase()).includes(input.toLowerCase())) {
              applyFilters([...appliedFilters, input]);
            }
          }}
          disabled={input === "" || isApplying}
          className="text-zinc-400 hover:text-zinc-300 disabled:text-zinc-600 flex justify-center items-center px-4 cursor-pointer"
        >
          {isApplying ? (
            <Loader2 className="h-[14px] w-[14px] animate-spin" />
          ) : filterError ? (
            <ShieldAlert className="text-red-600 h-[14px] w-[14px]" />
          ) : (
            <ArrowRight className="h-[14px] w-[14px]" />
          )}
        </button>
      </div>
      {filterError ? (
        <div className="flex items-center">
          <div className="w-full max-w-full text-zinc-500 font-mono text-[8px] font-medium truncate text-ellipsis overflow-hidden">{filterError}</div>
        </div>
      ) : null}
      {renderedFilters}
      <iframe
        sandbox="allow-modals allow-popups allow-scripts allow-same-origin allow-downloads"
        allow="clipboard-write"
        className="rounded h-full w-full"
        src={`/iframer?commit=${commitHash}#url=${encodeURIComponent(props.url)}`}
        ref={iframeRef}
      />
    </div>
  );

  function loadSuggestions(): Filter[] {
    try {
      const raw = localStorage.getItem(`subtrace.live.filters.${namespaceId}`);
      if (raw) {
        return JSON.parse(raw);
      }
    } catch {}

    const lhs1: string[] = ["request.method"];
    const op1: string[] = ["==", "!="];
    const rhs1 = ['"GET"', '"POST"', '"PUT"', '"DELETE"', '"PATCH"'];

    const lhs2: string[] = ["response.status"];
    const op2: string[] = ["=="];
    const rhs2 = ["200", "401", "403", "404", "500"];

    const lhs3: string[] = ["request.url"];
    const rest3: string[] = [
      '.startsWith("/api/users")',
      '.startsWith("/dashboard")',
      '.endsWith("/logout")',
      '.contains("login")',
      '.matches("^/products/\\\\d+$")',
      '.matches("/v[0-9]+/.*")',
    ];

    const lhs4: string[] = ["duration"];
    const op4: string[] = ["<", ">", "<=", ">="];
    const rhs4 = ["50", "100", "200", "500", "1000", "5000"];

    const defaults = [
      ...lhs1.map((lhs) => op1.map((op) => rhs1.map((rhs) => ({ text: `${lhs} ${op} ${rhs}`, kinds: [FilterKind.Method], useCount: 0, lastUsed: 0, builtIn: true })))).flat(2),
      ...lhs2.map((lhs) => op2.map((op) => rhs2.map((rhs) => ({ text: `${lhs} ${op} ${rhs}`, kinds: [FilterKind.Status], useCount: 0, lastUsed: 0, builtIn: true })))).flat(2),
      ...lhs3.map((lhs) => rest3.map((rest) => ({ text: `${lhs}${rest}`, kinds: [FilterKind.Url], useCount: 0, lastUsed: 0, builtIn: true }))).flat(2),
      ...lhs4.map((lhs) => op4.map((op) => rhs4.map((rhs) => ({ text: `${lhs} ${op} ${rhs}`, kinds: [FilterKind.Duration], useCount: 0, lastUsed: 0, builtIn: true })))).flat(2),
    ];

    localStorage.setItem(`subtrace.live.filters.${namespaceId}`, JSON.stringify(defaults));
    return defaults;
  }
}

export function LivePage(props: LivePageProps): React.ReactNode {
  const [url, setUrl] = React.useState<string | null>(null);

  const { currentNamespace } = CustomHooks.useNamespaceManager();
  const { orgId } = CustomHooks.useCurrentOrg();
  const { namespaceId } = currentNamespace;
  const { setupStatus, onSetupStatusChange } = props;

  React.useEffect(() => {
    const controller: AbortController = new AbortController();
    const { signal } = controller;

    async function populateSetupStatus(): Promise<void> {
      const request: Api.HasSeenPublisher_Request = { orgId };
      try {
        const response: Response = await ApiUtils.post("/api/HasSeenPublisher", request, { signal });
        await ApiUtils.assertStatus(response, 200);
        const { hasSeenPublisher }: Api.HasSeenPublisher_Response = await response.json();

        if (!hasSeenPublisher) {
          TelemetryUtils.log({ type: "onboarding_show" });
          const request: Api.GenerateNamespaceToken_Request = { namespaceId };
          const response: Response = await ApiUtils.post("/api/GenerateNamespaceToken", request, { signal });
          await ApiUtils.assertStatus(response, 200);
          const { token }: Api.GenerateNamespaceToken_Response = await response.json();

          onSetupStatusChange({ isComplete: false, namespaceToken: token });
        } else {
          onSetupStatusChange({ isComplete: true });
        }
      } catch (error: unknown) {
        if (signal.aborted) {
          // Do nothing, the API call was canceled
        } else {
          throw error;
        }
      }
    }

    populateSetupStatus();

    return (): void => {
      controller.abort("Cleaning up effect populateSetupStatus");
    };
  }, [namespaceId, onSetupStatusChange, orgId]);

  React.useEffect(() => {
    (async (): Promise<void> => {
      const body = JSON.stringify(pubsub.JoinSubscriber_Request.toJSON({ namespaceId }));
      const resp = await fetch(`/api/JoinSubscriber`, { method: "POST", body });
      const json = await resp.json();
      const { websocketUrl } = pubsub.JoinSubscriber_Response.fromJSON(json);
      setUrl(websocketUrl);
    })();
  }, [namespaceId]);

  if (setupStatus == null) {
    return null;
  }

  if (!url) {
    return <React.Fragment />;
  }

  return (
    <div className="m-2 flex w-full rounded-md">
      {setupStatus.isComplete ? null : (
        <div className="absolute inset-0">
          <div className="relative flex h-full w-full z-20 justify-center items-center rounded-md">
            {setupStatus.namespaceToken != null ? (
              <SetupInstructions namespaceToken={setupStatus.namespaceToken} onClose={() => onSetupStatusChange({ isComplete: true })} orgId={orgId} />
            ) : null}
          </div>
          <Overlay className="absolute inset-0 w-full h-full backdrop-blur-md backdrop-brightness-[0.85] rounded" />
        </div>
      )}
      <DevTools url={url} namespaceId={namespaceId} />
    </div>
  );
}

export type LivePageSetupStatus =
  | {
      isComplete: true;
    }
  | {
      isComplete: false;
      namespaceToken: string;
    };

export interface LivePageProps {
  setupStatus: LivePageSetupStatus | undefined;

  onSetupStatusChange: (setupStatus: LivePageSetupStatus) => void;
}
